export interface DisplayedColumnsActiveViewSyncState {
  activeViewStorageKey: string | null;
  initializedFromStorage: boolean;
}

export function shouldSkipDisplayedColumnsActiveViewSync(
  state: DisplayedColumnsActiveViewSyncState,
  activeViewStorageKey: string | null,
): boolean {
  if (
    state.initializedFromStorage &&
    activeViewStorageKey === state.activeViewStorageKey
  ) {
    return true;
  }

  state.activeViewStorageKey = activeViewStorageKey;
  state.initializedFromStorage = false;
  return false;
}

export interface DisplayedColumnLike {
  key: string;
  displayed: boolean;
}

/**
 * Adds keys that appeared in `columns` after `displayed` was seeded, each
 * placed after the nearest preceding column already displayed. A key nothing
 * precedes, such as a selection checkbox prepended once a flag resolves, goes
 * first rather than last.
 */
export function reconcileDisplayedColumns<T extends DisplayedColumnLike>(
  displayed: T[],
  columns: readonly { key: string; hidden?: boolean }[],
  make: (column: { key: string; hidden?: boolean }) => T,
): T[] {
  const displayedKeys = new Set(displayed.map((column) => column.key));
  const missing = columns.filter((column) => !displayedKeys.has(column.key));
  if (missing.length === 0) {
    return displayed;
  }
  const next = [...displayed];
  for (const column of missing) {
    const columnIndex = columns.indexOf(column);
    let insertAt = 0;
    for (let i = columnIndex - 1; i >= 0; i--) {
      const precedingKey = columns[i]?.key;
      const at = next.findIndex((c) => c.key === precedingKey);
      if (at >= 0) {
        insertAt = at + 1;
        break;
      }
    }
    next.splice(insertAt, 0, make(column));
  }
  return next;
}
